/**
 * Map Geometry Utilities
 * 
 * Utilities for calculating distances and generating curved paths on maps
 * using Turf.js for geospatial calculations.
 */

import * as turf from '@turf/turf';
import type { Feature, LineString } from 'geojson';

/**
 * Coordinate pair: [longitude, latitude]
 */
export type Coordinate = [number, number];

/**
 * GeoJSON Feature object (Turf.js return type for curved paths)
 * Uses GeoJSON spec types which support 2D and 3D coordinates
 */
export type GeoJSONFeature = Feature<LineString>;

/**
 * Calculate the distance along a curved path between two coordinates
 * 
 * Uses Turf.js to:
 * 1. Calculate center point between start/end
 * 2. Calculate bearing and offset for curve
 * 3. Generate Bezier spline curve
 * 4. Calculate total length of curved path
 * 
 * @param start - Starting coordinate [longitude, latitude]
 * @param end - Ending coordinate [longitude, latitude]
 * @returns Distance in miles along the curved path
 * 
 * @example
 * ```typescript
 * const distance = calculateDistance(
 *   [-79.3832, 43.6532],  // Toronto
 *   [-118.2437, 34.0522]  // Los Angeles
 * );
 * // Returns: ~2100 (miles)
 * ```
 */
export function calculateDistance(start: Coordinate, end: Coordinate): number {
  const { start: adjustedStart, end: adjustedEnd } = resolveAntimeridian(start, end);
  const startPoint = turf.point(adjustedStart);
  const endPoint = turf.point(adjustedEnd);
  const features = turf.featureCollection([startPoint, endPoint]);
  const center = turf.center(features);
  const midpoint = turf.point(center.geometry.coordinates);
  const bearing = turf.rhumbBearing(startPoint, center);
  const offsetDirection = bearing > 0 ? bearing - 90 : bearing + 90;
  const distance = turf.rhumbDistance(startPoint.geometry.coordinates, endPoint, { units: 'miles' });
  const offsetDistance = distance * 0.10;
  const offset = turf.rhumbDestination(midpoint, offsetDistance, offsetDirection, { units: 'miles' });
  const line = turf.lineString([startPoint.geometry.coordinates, offset.geometry.coordinates, endPoint.geometry.coordinates]);
  const curved = turf.bezierSpline(line, { resolution: 2000, sharpness: 1.0 });
  return turf.length(curved, { units: 'miles' });
}

/**
 * Generate a curved GeoJSON path between two coordinates
 * 
 * Creates a Bezier spline curve that can be rendered on Mapbox GL maps.
 * The curve is offset perpendicular to the straight line to create a visual arc.
 * 
 * @param start - Starting coordinate [longitude, latitude]
 * @param end - Ending coordinate [longitude, latitude]
 * @returns GeoJSON Feature with curved LineString, or false if curve is invalid
 * 
 * @remarks
 * Returns `false` if the curved path is more than 50% longer than straight-line distance,
 * which indicates an invalid curve (can happen with antipodal points).
 * 
 * @example
 * ```typescript
 * const path = generatePath(
 *   [-79.3832, 43.6532],  // Toronto
 *   [-118.2437, 34.0522]  // Los Angeles
 * );
 * 
 * if (path) {
 *   map.addSource('route', {
 *     type: 'geojson',
 *     data: { type: 'FeatureCollection', features: [path] }
 *   });
 * }
 * ```
 */
export function generatePath(start: Coordinate, end: Coordinate): GeoJSONFeature | false {
  const { start: adjustedStart, end: adjustedEnd } = resolveAntimeridian(start, end);
  const startPoint = turf.point(adjustedStart);
  const endPoint = turf.point(adjustedEnd);
  const features = turf.featureCollection([startPoint, endPoint]);
  const center = turf.center(features);
  const midpoint = turf.point(center.geometry.coordinates);
  const bearing = turf.rhumbBearing(startPoint, center);
  const offsetDirection = bearing > 0 ? bearing - 90 : bearing + 90;
  const distance = turf.rhumbDistance(startPoint.geometry.coordinates, endPoint, { units: 'miles' });
  const offsetDistance = distance * 0.10;
  const offset = turf.rhumbDestination(midpoint, offsetDistance, offsetDirection, { units: 'miles' });
  const line = turf.lineString([startPoint.geometry.coordinates, offset.geometry.coordinates, endPoint.geometry.coordinates]);
  const curved = turf.bezierSpline(line, { resolution: 2000, sharpness: 1.0 });
  const length = turf.length(curved, { units: 'miles' });

  // Validate curve - if it's more than 50% longer than straight distance, reject it
  if ((distance - length) / distance < -0.5) {
    return false;
  }

  // Ensure endpoint is included in the path
  curved.geometry.coordinates.push(endPoint.geometry.coordinates);
  
  // Filter out any invalid coordinates (NaN, Infinity) but preserve out-of-bounds
  // longitudes for antimeridian crossings - Mapbox handles wrapping automatically
  curved.geometry.coordinates = curved.geometry.coordinates.filter(coord => {
    const [lng, lat] = coord;
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
      console.warn('[generatePath] Invalid coordinate filtered:', coord);
      return false;
    }
    return true;
  }) as [number, number][];
  
  return curved;
}

/**
 * Calculate the straight-line distance between two coordinates
 * (Great circle distance using Haversine formula)
 * 
 * @param start - Starting coordinate [longitude, latitude]
 * @param end - Ending coordinate [longitude, latitude]
 * @returns Distance in miles
 * 
 * @example
 * ```typescript
 * const straightDistance = calculateStraightDistance(
 *   [-79.3832, 43.6532],  // Toronto
 *   [-118.2437, 34.0522]  // Los Angeles
 * );
 * // Returns: ~2010 (miles, as the crow flies)
 * ```
 */
export function calculateStraightDistance(start: Coordinate, end: Coordinate): number {
  const { start: adjustedStart, end: adjustedEnd } = resolveAntimeridian(start, end);
  return turf.distance(adjustedStart, adjustedEnd, { units: 'miles' });
}

/**
 * Ensure we always draw the shortest path across the globe by avoiding
 * antimeridian wrap (e.g., forcing -170° → 170° trips to go the 20° way,
 * not the 340° way). Adjusts longitude only; latitude is untouched.
 */
function resolveAntimeridian(start: Coordinate, end: Coordinate): { start: Coordinate; end: Coordinate } {
  const [startLng, startLat] = start;
  const [endLng, endLat] = end;
  const diff = endLng - startLng;

  if (Math.abs(diff) <= 180) {
    return { start, end };
  }

  const adjustedEndLng = diff > 0 ? endLng - 360 : endLng + 360;
  return {
    start,
    end: [adjustedEndLng, endLat] as Coordinate
  };
}
